/**
 * MasteringModal — one-tap AI mastering for a single track.
 *
 * Flow: user opens modal → picks smart (default) or reference (upload ref file)
 * → we POST to /tracks/{id}/master which returns a job_id → we poll every 3s
 * until status flips to done or failed → user hears the mastered preview and
 * either downloads it, applies it (replaces original), or discards.
 */
import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  Wand2, Sparkles, Upload, Download, Play, Pause, Loader2, RefreshCw, Check, Info,
} from "lucide-react";

export default function MasteringModal({ track, onClose, onApplied }) {
  const [mode, setMode] = useState("smart");
  const [refFile, setRefFile] = useState(null);
  const [job, setJob] = useState(null);       // in-flight job doc
  const [applying, setApplying] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const pollRef = useRef(null);

  const start = async () => {
    if (mode === "reference" && !refFile) {
      toast.error("Upload a reference track first");
      return;
    }
    const fd = new FormData();
    fd.append("mode", mode);
    if (refFile) fd.append("reference", refFile);
    try {
      const { data } = await api.post(`/tracks/${track.id}/master`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob({ id: data.job_id, status: "running" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Poll every 3s while a job is running. Stops itself on done/failed.
  useEffect(() => {
    if (!job || job.status !== "running") return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/mastering/jobs/${job.id}`);
        setJob(data);
        if (data.status !== "running") clearInterval(pollRef.current);
      } catch { /* transient; keep polling */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [job]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const apply = async () => {
    if (!job || job.status !== "done") return;
    if (!window.confirm("Replace the original track with the mastered version? This can't be undone.")) return;
    setApplying(true);
    try {
      await api.post(`/tracks/${track.id}/master/apply/${job.id}`);
      toast.success("Mastered version is now your track file");
      onApplied && onApplied(job.mastered_path);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setApplying(false); }
  };

  const download = () => {
    if (!job || !job.mastered_path) return;
    window.open(fileUrl(job.mastered_path), "_blank", "noopener,noreferrer");
  };

  const isRunning = job && job.status === "running";
  const isDone = job && job.status === "done";
  const isFailed = job && job.status === "failed";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="mastering-modal"
        className="rounded-none bg-[#0A0A0A] border-[#2A2A2A] max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-[#FF4500]" /> AI Master
          </DialogTitle>
          <div className="font-mono text-[11px] text-gray-500 mt-1 truncate">
            "{track.title}" · free forever on MeloCraft
          </div>
        </DialogHeader>

        {!job && (
          <>
            {/* Mode picker */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                data-testid="mastering-mode-smart"
                onClick={() => setMode("smart")}
                className={`p-3 border text-left transition-colors ${
                  mode === "smart"
                    ? "border-[#FF4500] bg-[#FF4500]/[0.06]"
                    : "border-[#2A2A2A] hover:border-white/30"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="h-4 w-4 text-[#FF4500]" />
                  <div className="font-display text-sm">Smart Master</div>
                </div>
                <div className="font-mono text-[10px] text-gray-500">
                  Auto-tuned to your genre. ~15 sec.
                </div>
              </button>
              <button
                data-testid="mastering-mode-reference"
                onClick={() => setMode("reference")}
                className={`p-3 border text-left transition-colors ${
                  mode === "reference"
                    ? "border-[#FF4500] bg-[#FF4500]/[0.06]"
                    : "border-[#2A2A2A] hover:border-white/30"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Upload className="h-4 w-4 text-[#FF4500]" />
                  <div className="font-display text-sm">Studio Master</div>
                </div>
                <div className="font-mono text-[10px] text-gray-500">
                  Match a reference track. ~60 sec.
                </div>
              </button>
            </div>

            {mode === "reference" && (
              <div className="mt-4 border border-[#2A2A2A] p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">
                  Reference track (a song you want yours to sound like)
                </div>
                <input
                  data-testid="mastering-reference-file"
                  type="file"
                  accept=".wav,.mp3,.flac,.aiff,.m4a,audio/*"
                  onChange={(e) => setRefFile(e.target.files?.[0] || null)}
                  className="w-full font-mono text-xs bg-black border border-[#2A2A2A] p-2"
                />
                <div className="font-mono text-[10px] text-gray-500 mt-2 flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  Pick a professionally-mastered song you love. Max 25MB. Nothing is saved after mastering finishes.
                </div>
              </div>
            )}

            <div className="mt-5">
              <Button
                data-testid="mastering-start"
                onClick={start}
                className="w-full rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11"
              >
                <Wand2 className="h-4 w-4 mr-2" /> Master this track
              </Button>
              <div className="mt-2 font-mono text-[10px] text-gray-500 text-center">
                Free for all MeloCraft artists — no limits.
              </div>
            </div>
          </>
        )}

        {isRunning && (
          <div className="py-10 text-center" data-testid="mastering-running">
            <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-[#FF4500]" />
            <div className="font-display text-lg">Mastering your track...</div>
            <div className="font-mono text-[10px] text-gray-500 mt-1">
              {mode === "reference" ? "Matching to your reference — takes about a minute" : "Applying the smart chain — takes about 15 seconds"}
            </div>
          </div>
        )}

        {isDone && (
          <div className="mt-4" data-testid="mastering-done">
            <div className="border border-green-500/40 bg-green-500/[0.05] p-3 mb-3 flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />
              <div className="font-display text-sm">Master complete</div>
              {job.preset && (
                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  {job.preset}
                </span>
              )}
            </div>
            <div className="border border-[#2A2A2A] p-3 flex items-center gap-3">
              <button
                data-testid="mastering-preview-play"
                onClick={togglePlay}
                className="h-10 w-10 grid place-items-center bg-[#FF4500] hover:bg-[#E53E00] text-black shrink-0"
              >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="font-display text-sm truncate">Mastered preview</div>
                <div className="font-mono text-[10px] text-gray-500">Listen before you apply</div>
              </div>
              <audio
                ref={audioRef}
                src={fileUrl(job.mastered_path)}
                onEnded={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button
                data-testid="mastering-download"
                onClick={download}
                variant="outline"
                className="rounded-none border-[#2A2A2A] bg-transparent hover:border-[#FF4500] hover:text-[#FF4500] font-mono text-xs uppercase tracking-widest h-10"
              >
                <Download className="h-3.5 w-3.5 mr-2" /> Download
              </Button>
              <Button
                data-testid="mastering-apply"
                onClick={apply}
                disabled={applying}
                className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-10"
              >
                {applying ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-2" />}
                Apply as track
              </Button>
            </div>
            <button
              data-testid="mastering-remaster"
              onClick={() => setJob(null)}
              className="mt-3 w-full font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-white flex items-center justify-center gap-1"
            >
              <RefreshCw className="h-3 w-3" /> Try a different master
            </button>
          </div>
        )}

        {isFailed && (
          <div className="mt-4 border border-red-500/40 bg-red-500/[0.05] p-4" data-testid="mastering-failed">
            <div className="font-display text-sm text-red-400 mb-1">Mastering failed</div>
            <div className="font-mono text-[11px] text-gray-400 mb-3">
              {job.error || "Something went wrong. Try again in a moment."}
            </div>
            <Button
              onClick={() => setJob(null)}
              className="rounded-none bg-[#FF4500] text-black font-mono text-xs uppercase tracking-widest h-9"
            >
              Try again
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
